<script setup lang="ts">
import { Chart } from "chart.js";
import { v4 as uuidv4 } from "uuid";
import type { DtwinMetricsCounts } from "~/modules/dashboard/models/DtwinMetricsCounts.type";

const props = defineProps<{
  dtwinMetricsCounts: DtwinMetricsCounts;
  dtwinModelFriendlyName: string;
  backgroundColors: string[];
}>();

const chartId = `chart-${uuidv4()}`;

const chartRef = ref<HTMLCanvasElement>();

const chartInstance = ref<Chart<"doughnut", number[], string> | undefined>();

const centerTextPlugin = {
  id: "centerText",
  beforeDraw(chart: Chart) {
    const { height } = chart;
    const ctx = chart.ctx;
    ctx.restore();

    const fontSize = 16;
    const fontFamily = "Montserrat";
    const fontWeight = 600;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillStyle = "#002F5D";

    const text = props.dtwinModelFriendlyName;

    const meta = chart.getDatasetMeta(0);
    const firstArc = meta.data[0];

    const textX = firstArc.x;
    const textY = height / 2;

    ctx.fillText(text, textX, textY);
    ctx.save();
  },
};

const dataChart = computed(() => {
  return {
    labels: Object.keys(props.dtwinMetricsCounts).map((label: string) =>
      // Add spaces at the end of the labels in order to take all the width they can in the legend
      // It enables to make the size of the doughnut non variable
      // 50 is an arbitrary value which enables to increase the legend witdh at its maximum value, greater values also work
      label.padEnd(50, " "),
    ),
    datasets: [
      {
        data: Object.values(props.dtwinMetricsCounts),
        backgroundColor: props.backgroundColors,
      },
    ],
  };
});

const buildChart = () => {
  const chartCtx = chartRef.value?.getContext("2d");

  if (chartCtx) {
    chartInstance.value = new Chart(chartCtx, {
      type: "doughnut",
      data: dataChart.value,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "75%",
        plugins: {
          legend: {
            position: "right",
            labels: {
              pointStyle: "circle",
              usePointStyle: true,
              boxWidth: 8,
              boxHeight: 8,
              font: {
                size: 14,
                style: "normal",
                weight: "400",
              },
              color: "#475467",
            },
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                // trim the label because of the added spaces at the end of the labels
                return `${context.label.trim() ?? ""}: ${context.formattedValue ?? ""}`;
              },
            },
          },
        },
      },
      plugins: [centerTextPlugin],
    });
  }
};

watch(() => props.dtwinMetricsCounts, buildChart);

onMounted(buildChart);
</script>

<template>
  <div class="flex justify-center">
    <canvas :id="chartId" ref="chartRef" height="140" />
  </div>
</template>
